import { pvpCardSlotViews } from '@kenocyber/game-domain/pvp';
import type { AssetManifest } from '@kenocyber/shared';
import {
  decodeOpenWorldFieldReward,
  findFishSpecies,
  type ItemCategory,
  itemDisplayName,
  OPEN_WORLD_ENTRY_SIDES,
  OPEN_WORLD_MAX_PARTY_MEMBERS,
  type OpenWorldBorderState,
  type OpenWorldEntrySide,
  type OpenWorldExtractionReceipt,
  type OpenWorldFieldItem,
  type OpenWorldFishingRefusalFrame,
  type OpenWorldFishingResult,
  type OpenWorldLootRefusal,
  type OpenWorldLootResult,
  type OpenWorldPartyBlocker,
  type OpenWorldPartyMemberState,
  type OpenWorldSaleLine,
  type OpenWorldSaleRefusal,
  openWorldQuadrantLabel,
} from '@kenocyber/shared';
import { type ReactElement, useEffect, useState } from 'react';
import { fishArtUrl, itemArtUrl } from '../game/assets';
import type { GameBridge } from '../game/bridge';
import { CardHudBar } from '../ui/CardHudBar';
import { rarityLabel } from '../ui/rarity';
import { OpenWorldMapPanel, OpenWorldMinimap } from './OpenWorldMap';
import type { OpenWorldSession } from './useOpenWorld';

/**
 * **A interface do mundo aberto: entrada, HUD, fronteira e relatório.**
 *
 * ## O que ela deliberadamente não tem
 *
 * Barra de cartas — as cartas não estão ligadas ao runtime do mundo aberto, e
 * uma barra que mostrasse três espaços que não fazem nada seria uma promessa
 * falsa. Mapa, minimapa e arte final também não: este gate entrega o primeiro
 * ciclo jogável, e um mapa é a decisão de outro.
 *
 * ## Onde ela vive
 *
 * Flutuando sobre a arena, como todos os painéis deste jogo: abrir um nunca
 * redimensiona nem recria o canvas. O HUD encosta-se aos cantos e deixa o meio
 * livre, porque o meio é onde a nave está.
 */

const SIDE_LABELS: Readonly<Record<OpenWorldEntrySide, string>> = Object.freeze({
  north: 'NORTE',
  south: 'SUL',
  east: 'LESTE',
  west: 'OESTE',
});

export function OpenWorldOpenButton({
  enabled,
  onOpen,
}: {
  readonly enabled: boolean;
  readonly onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="arena-tab open-world-open-button"
      data-testid="open-world-open"
      disabled={!enabled}
      onClick={onOpen}
    >
      MUNDO ABERTO
    </button>
  );
}

/**
 * **O painel de entrada.** Quatro lados, a capacidade, o aviso, e a confirmação.
 *
 * A capacidade é mostrada **antes** de a expedição começar porque é aí que ela
 * congela: vê-la depois seria vê-la tarde demais para decidir seja o que for.
 *
 * O aviso é dito por extenso e não escondido num ícone. O que se leva para um
 * quadrante perigoso perde-se ao morrer, e um jogador que não soubesse disso
 * teria sido enganado por omissão.
 */
export function OpenWorldEntryPanel({
  session,
  onClose,
}: {
  readonly session: OpenWorldSession;
  readonly onClose: () => void;
}) {
  const [chosen, setChosen] = useState<OpenWorldEntrySide | null>(null);
  useEffect(() => {
    void session.refresh();
  }, [session.refresh]);

  const entry = session.entry;
  const active = entry?.active ?? null;

  return (
    <section className="open-world-panel" data-testid="open-world-panel">
      <header className="open-world-panel__head">
        <h2>MUNDO ABERTO</h2>
        <button type="button" onClick={onClose} data-testid="open-world-close">
          FECHAR
        </button>
      </header>

      {active === null && entry !== null && entry.lastReceipt !== null ? (
        <OpenWorldLastReceipt receipt={entry.lastReceipt} />
      ) : null}
      {active !== null ? (
        <div className="open-world-panel__resume">
          <p>
            Expedição a decorrer em <strong>{active.label}</strong>.
          </p>
          <button
            type="button"
            data-testid="open-world-resume"
            disabled={session.busy}
            onClick={() => void session.enter('north')}
          >
            RETOMAR EXPEDIÇÃO
          </button>
        </div>
      ) : (
        <div className="open-world-panel__enter">
          <p className="open-world-panel__capacity" data-testid="open-world-capacity">
            {/*
              Um traço enquanto a leitura não chegou, e nunca um zero: um zero é um
              número, e um número errado sobre a capacidade é a única coisa deste
              painel que alguém usaria para decidir.
            */}
            Capacidade de carga: <strong>{entry === null ? '—' : entry.capacity}</strong> espaços
          </p>
          {/*
            **A cadência que a nave vai MESMO ter lá dentro** (gate V28I.AE).

            O mundo aberto tem um teto de salvas por segundo, e mostrar a
            cadência do equipamento enquanto a sala aplica outra seria mentir ao
            jogador sobre a nave dele. Quando o teto morde, a frase diz-o por
            palavras — nunca só por cor — e diz também o que a nave pedia, para
            que a diferença seja legível em vez de misteriosa.

            Uma linha no painel que já existe. Nada é redesenhado, nada de novo é
            estilizado, e não se diz nada sobre a lotação: quantos cabem é uma
            decisão interna e não uma coisa que se anuncie a quem chega.
          */}
          {entry !== null && entry.cadence !== null ? (
            <p className="open-world-panel__capacity" data-testid="open-world-cadence">
              Cadência no mundo aberto:{' '}
              <strong>{formatVolleys(entry.cadence.effectiveMilliPerSecond)}</strong> salvas/s
              {openWorldCadenceLimitText(entry.cadence)}
            </p>
          ) : null}
          {/*
            O aviso, por extenso. O que se leva perde-se ao morrer, e uma caixa
            pública fica onde a nave caiu para quem lá chegar primeiro.
          */}
          <p className="open-world-panel__warning" data-testid="open-world-warning">
            Recompensas de campo podem ser perdidas. Morrer deixa tudo o que trouxer numa caixa
            pública, no sítio onde a nave cair.
          </p>
          <div className="open-world-panel__sides">
            {OPEN_WORLD_ENTRY_SIDES.map((side) => (
              <button
                key={side}
                type="button"
                className={`open-world-side${chosen === side ? ' is-active' : ''}`}
                data-testid={`open-world-side-${side}`}
                onClick={() => setChosen(side)}
              >
                {SIDE_LABELS[side]}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="open-world-confirm"
            data-testid="open-world-confirm"
            disabled={chosen === null || session.busy || !(entry?.canEnter ?? false)}
            onClick={() => {
              if (chosen !== null) {
                void session.enter(chosen);
              }
            }}
          >
            {session.busy ? 'A PARTIR…' : 'CONFIRMAR PARTIDA'}
          </button>
        </div>
      )}
      {/*
        **A PT vive ao lado da entrada, e não noutro ecrã.**

        Porque as duas decisões são a mesma: com quem vou, e por que lado. Um
        painel separado obrigaria a fechar um para ver o outro, e um jogador que
        marcasse pronto sem ver a capacidade da própria carga estaria a decidir
        às cegas.
      */}
      <OpenWorldPartyPanel session={session} />
      {session.error !== null ? (
        <p className="open-world-panel__error" data-testid="open-world-error">
          {session.error}
        </p>
      ) : null}
    </section>
  );
}
/**
 * **Cada estado de integrante, em palavras** (gate V28I.Q.1).
 *
 * Uma tabela fechada sobre a enumeração do servidor: um nono estado não
 * compila sem ter aqui a frase que o descreve, o que é exatamente a garantia
 * que se quer — um estado sem nome apareceria como um espaço vazio numa lista
 * onde a diferença entre "atravessando" e "morto" importa.
 *
 * **Nunca só uma cor.** A cor reforça; a palavra é a informação.
 */
const PARTY_STATE_TEXT: Readonly<Record<OpenWorldPartyMemberState, string>> = {
  no_lobby: 'NO LOBBY',
  entering: 'ENTRANDO',
  active: 'ATIVO',
  crossing: 'EM TRAVESSIA',
  disconnected: 'DESCONECTADO',
  dead: 'MORTO',
  extracted: 'EXTRAÍDO',
  left: 'ABANDONOU',
};

/**
 * **Onde ele está**, como um rótulo, e o que esse sítio é.
 *
 * `A/3 PORTO` ou `F/7 PERIGOSO`. Um travessão quando não há quadrante — quem
 * extraiu ou morreu não está em sítio nenhum, e escrever o último onde esteve
 * seria a interface a afirmar uma coisa que a autoridade não diz.
 */
function partyPlaceText(member: {
  readonly quadrant: { readonly rowIndex: number; readonly columnIndex: number } | null;
  readonly safe: boolean | null;
}): string {
  if (member.quadrant === null) {
    return '—';
  }
  return `${openWorldQuadrantLabel(member.quadrant)} ${member.safe === true ? 'PORTO' : 'PERIGOSO'}`;
}
/**
 * **A área `PT / EXPEDIÇÃO` do painel de entrada.**
 *
 * ## O que ela mostra, e de onde vem
 *
 * Do servidor, e de mais nada. A lista, o líder, a prontidão, o lado e os
 * convites são recalculados a cada ação e devolvidos inteiros; esta função
 * desenha o que recebeu. Ela não sabe quem é o próprio jogador — o servidor
 * marca-o com `self` — nem decide quem pode iniciar: `blocked` chega já
 * resolvido.
 *
 * ## Nunca só por cor
 *
 * Pronto e por confirmar dizem-se por palavras (`PRONTO`, `A AGUARDAR`), o
 * líder leva a palavra `LÍDER`, e o que impede o início é uma frase. A cor é um
 * reforço, e um jogador que não a distinga lê exatamente a mesma coisa.
 *
 * ## Um nome longo não parte a coluna
 *
 * O nome é truncado por CSS e o completo fica no `title`, que é o que um leitor
 * de ecrã e um rato parado sabem ler. Truncar no servidor teria apagado o nome
 * verdadeiro de quem o tem longo.
 */
function OpenWorldPartyPanel({ session }: { readonly session: OpenWorldSession }): ReactElement {
  const [name, setName] = useState('');
  const view = session.party;
  const party = view?.party ?? null;
  const busy = session.busy;

  /* O estado, em palavras. Uma frase por situação, e nunca um código. */
  const status =
    party === null
      ? view !== null && view.received.length > 0
        ? 'CONVITE RECEBIDO'
        : 'SOLO'
      : party.expeditionActive
        ? 'EXPEDIÇÃO EM ANDAMENTO'
        : party.members.find((member) => member.self)?.ready === true
          ? 'PRONTO'
          : party.blocked.length === 0
            ? 'NA PT'
            : 'AGUARDANDO';

  return (
    <section className="open-world-party" data-testid="open-world-party">
      <header className="open-world-party__head">
        <h3>PT / EXPEDIÇÃO</h3>
        <span className="open-world-party__status" data-testid="open-world-party-status">
          {status}
        </span>
      </header>

      {party === null ? (
        <>
          <p className="open-world-party__hint">
            Voar sozinho é uma expedição de um integrante. Uma PT leva até{' '}
            {OPEN_WORLD_MAX_PARTY_MEMBERS}.
          </p>
          <button
            type="button"
            className="open-world-party__action"
            data-testid="open-world-party-create"
            disabled={busy}
            onClick={() => void session.partyAct({ do: 'create' })}
          >
            CRIAR PT
          </button>
        </>
      ) : (
        <>
          {/*
            **Quantos são, quantos cabem, e quando já não cabe mais**
            (gate V28I.AA).

            O máximo vem da constante canónica e nunca de um número escrito aqui:
            um painel que prometesse um teto diferente do que a autoridade aplica
            seria o pior sítio para descobrir a diferença — depois de convidar.

            **Por palavras, e não por cor.** "PT CHEIA" lê-se num monitor a preto
            e branco, e a recusa do servidor diz o mesmo se alguém tentar na
            mesma. A classe é a que o painel já usa: nada foi redesenhado.
          */}
          <p className="open-world-party__hint" data-testid="open-world-party-count">
            Integrantes: <strong>{party.members.length}</strong> de {OPEN_WORLD_MAX_PARTY_MEMBERS}
            {party.members.length >= OPEN_WORLD_MAX_PARTY_MEMBERS ? ' — PT CHEIA.' : null}
          </p>
          <ul className="open-world-party__members" data-testid="open-world-party-members">
            {party.members.map((member) => (
              <li key={member.id} className="open-world-party__member">
                <span className="open-world-party__name" title={member.name}>
                  {member.name}
                </span>
                {member.leader ? <span className="open-world-party__tag">LÍDER</span> : null}
                <span className="open-world-party__tag">{PARTY_STATE_TEXT[member.state]}</span>
                {member.quadrant !== null ? (
                  <span className="open-world-party__tag">{partyPlaceText(member)}</span>
                ) : null}
                {party.expeditionActive ? null : (
                  <span className="open-world-party__tag">
                    {member.ready ? 'PRONTO' : 'A AGUARDAR'}
                  </span>
                )}
                {member.blocker !== null ? (
                  <span className="open-world-party__tag is-blocked">
                    {blockerText(member.blocker)}
                  </span>
                ) : null}
                {party.leader && !member.self && !party.expeditionActive ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void session.partyAct({ do: 'kick', memberId: member.id })}
                  >
                    EXPULSAR
                  </button>
                ) : null}
              </li>
            ))}
          </ul>

          {/*
            **O lado da PT, e ele é do servidor.**

            Os botões de lado do topo do painel são da entrada SOLO e ficam em
            estado local até à confirmação — é assim que sempre funcionaram, e
            não mudam. Estes são outros: cada um manda `do: 'side'`, e o que
            fica marcado é o que a resposta trouxer. Enquanto o servidor não
            responder, o marcado continua a ser o anterior — um lado que se
            pintasse por otimismo mostraria a toda a PT um destino que o líder
            julgou ter escolhido.

            Quem não lidera não vê botão nenhum: um botão que finge poder
            mudar uma coisa é pior do que a sua ausência.
          */}
          {party.leader && !party.expeditionActive ? (
            <div className="open-world-party__sides" data-testid="open-world-party-sides">
              <span className="open-world-party__hint">Lado de entrada da PT</span>
              {OPEN_WORLD_ENTRY_SIDES.map((side) => (
                <button
                  key={side}
                  type="button"
                  className={`open-world-side${party.side === side ? ' is-active' : ''}`}
                  data-testid={`open-world-party-side-${side}`}
                  disabled={busy}
                  onClick={() => void session.partyAct({ do: 'side', side })}
                >
                  {SIDE_LABELS[side]}
                </button>
              ))}
            </div>
          ) : null}

          <p className="open-world-party__hint">
            Lado de entrada: <strong>{SIDE_LABELS[party.side]}</strong>
            {party.expeditionActive
              ? ' — congelado durante a expedição.'
              : party.leader
                ? ' — escolhido por si, e visto por toda a PT.'
                : ' — escolhido pelo líder.'}
          </p>

          {party.leader &&
          !party.expeditionActive &&
          party.members.length < OPEN_WORLD_MAX_PARTY_MEMBERS ? (
            <div className="open-world-party__invite">
              <label htmlFor="open-world-party-name">Convidar pelo nome exato</label>
              <input
                id="open-world-party-name"
                type="text"
                value={name}
                maxLength={64}
                onChange={(event) => setName(event.target.value)}
              />
              <button
                type="button"
                data-testid="open-world-party-invite"
                disabled={busy || name.trim().length === 0}
                onClick={() => {
                  void session.partyAct({ do: 'invite', name: name.trim() });
                  setName('');
                }}
              >
                CONVIDAR
              </button>
            </div>
          ) : null}

          {party.sent.length === 0 ? null : (
            <ul className="open-world-party__invites">
              {party.sent.map((invite) => (
                <li key={invite.id}>
                  <span className="open-world-party__name" title={invite.toName}>
                    {invite.toName}
                  </span>
                  <span className="open-world-party__tag">
                    expira em {Math.ceil(invite.ttlMs / 60_000)} min
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void session.partyAct({ do: 'cancel', inviteId: invite.id })}
                  >
                    CANCELAR CONVITE
                  </button>
                </li>
              ))}
            </ul>
          )}

          {party.blocked.length === 0 ? null : (
            <p className="open-world-party__blocked" data-testid="open-world-party-blocked">
              {party.blocked.map((blocker) => blockerText(blocker)).join(' · ')}
            </p>
          )}

          <div className="open-world-party__actions">
            {party.expeditionActive ? null : (
              <>
                <button
                  type="button"
                  data-testid="open-world-party-ready"
                  disabled={busy}
                  onClick={() =>
                    void session.partyAct({
                      do: 'ready',
                      ready: party.members.find((member) => member.self)?.ready !== true,
                    })
                  }
                >
                  {party.members.find((member) => member.self)?.ready === true
                    ? 'JÁ NÃO ESTOU PRONTO'
                    : 'PRONTO'}
                </button>
                {party.leader ? (
                  <button
                    type="button"
                    className="open-world-party__start"
                    data-testid="open-world-party-start"
                    disabled={busy || party.blocked.length > 0}
                    onClick={() =>
                      void session.partyAct({ do: 'start', commandId: crypto.randomUUID() })
                    }
                  >
                    INICIAR EXPEDIÇÃO
                  </button>
                ) : null}
              </>
            )}
            <button
              type="button"
              data-testid="open-world-party-leave"
              disabled={busy || party.expeditionActive}
              onClick={() => void session.partyAct({ do: 'leave' })}
            >
              SAIR
            </button>
          </div>
        </>
      )}

      {(view?.received ?? []).length === 0 ? null : (
        <ul className="open-world-party__invites" data-testid="open-world-party-received">
          {(view?.received ?? []).map((invite) => (
            <li key={invite.id}>
              <span className="open-world-party__name" title={invite.fromName}>
                {invite.fromName}
              </span>
              <span className="open-world-party__tag">convidou-o</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void session.partyAct({ do: 'accept', inviteId: invite.id })}
              >
                ACEITAR
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void session.partyAct({ do: 'decline', inviteId: invite.id })}
              >
                RECUSAR
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * **Milésimos de salva por segundo, em palavras** (gate V28I.AE).
 *
 * O que atravessa a rede é um inteiro — 540 540 são 540,54 salvas por segundo —
 * porque uma vírgula flutuante num contrato é uma vírgula flutuante a divergir
 * entre duas máquinas. A vírgula nasce aqui, na frase, e em mais lado nenhum.
 *
 * Duas casas, e a vírgula decimal do português.
 */
function formatVolleys(milliPerSecond: number): string {
  return (milliPerSecond / 1000).toFixed(2).replace('.', ',');
}

/**
 * **O que se acrescenta à cadência quando o mundo a limita** (gate V28I.CF.3).
 *
 * Uma frase, e não uma cor: quem chega tem de conseguir LER que o mundo tem um
 * teto, e tem de continuar a ver o que a sua nave pede. Esconder o pedido, ou
 * substituí-lo pelo efetivo, seria o atributo da conta a mentir.
 *
 * Exportada porque é o que se prova; o parágrafo à volta é apresentação.
 */
export function openWorldCadenceLimitText(cadence: {
  readonly limited: boolean;
  readonly shipMilliPerSecond: number;
}): string {
  return cadence.limited
    ? ` — LIMITADA PELO MUNDO ABERTO (a sua nave pede ${formatVolleys(cadence.shipMilliPerSecond)}).`
    : '.';
}

/** O que impede o início, em palavras. Exaustiva sobre a lista fechada. */
function blockerText(blocker: OpenWorldPartyBlocker): string {
  switch (blocker) {
    case 'not_ready':
      return 'Falta alguém confirmar.';
    case 'in_world':
      return 'Alguém já está no mundo aberto.';
    case 'in_duel':
      return 'Alguém está num duelo.';
    case 'receipt_pending':
      return 'Alguém tem um recibo por ler.';
    case 'hunt_busy':
      return 'A caça de alguém não fechou.';
  }
}

/**
 * **O HUD mínimo.** Nos cantos, e nunca por cima da nave.
 *
 * Só o que ajuda a decidir: onde se está, se é seguro, quanta vida resta, o que
 * se traz, e o que a fronteira está a fazer. Tudo o resto é ruído sobre um sítio
 * que se atravessa em quatro minutos.
 */
export function OpenWorldHud({
  session,
  bridge,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly bridge: GameBridge;
  /**
   * O manifesto geral, para a carga e a caixa desenharem a arte de um item.
   *
   * `null` enquanto ele ainda não chegou: as duas listas continuam legíveis pelo
   * nome canónico, que não depende de manifesto nenhum.
   */
  readonly manifest: AssetManifest | null;
}) {
  const state = session.state;
  const border = state.border;
  const cargo = state.cargo;

  /**
   * **O mapa completo: um botão, e a tecla `M`.**
   *
   * `M` está livre: o movimento é WASD, o disparo é o rato, e nada mais neste
   * modo lê o teclado. Uma tecla que já significasse outra coisa teria de
   * significar duas.
   *
   * As quatro recusas são deliberadas, e cada uma tem a sua razão:
   *
   * - **`repeat`**: uma tecla segura repete dezenas de vezes por segundo, e
   *   sem isto o mapa abria e fechava enquanto o dedo estivesse em baixo;
   * - **modificadores**: `Ctrl+M`, `Alt+M` e `Meta+M` pertencem ao navegador e
   *   ao sistema, e roubá-los é roubar um atalho que o jogador já tem;
   * - **campos editáveis**: escrever `m` num nome de convite não é pedir um
   *   mapa;
   * - **`Escape` só fecha a camada de cima**: enquanto o mapa está aberto, ele
   *   é essa camada, e nenhum outro painel o vê passar.
   */
  const [mapOpen, setMapOpen] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
        return;
      }
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return;
        }
      }
      if (event.code === 'KeyM') {
        event.preventDefault();
        setMapOpen((open) => !open);
        return;
      }
      if (event.key === 'Escape' && mapOpen) {
        /*
         * Só a camada de cima. `stopPropagation` impede que o mesmo `Escape`
         * feche também o painel que está por baixo — fechar dois painéis com
         * uma tecla é perder um deles sem o ter pedido.
         */
        event.preventDefault();
        event.stopPropagation();
        setMapOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [mapOpen]);
  const me = state.entities.find((entity) => entity.id === state.me) ?? null;
  const health =
    me === null ? 0 : Number((BigInt(me.healthMilli) * 100n) / (BigInt(me.maxHealthMilli) || 1n));

  return (
    <div className="open-world-hud" data-testid="open-world-hud">
      <div className="open-world-hud__corner open-world-hud__corner--left">
        <span className="open-world-hud__coord" data-testid="open-world-coord">
          {state.label || '—'}
        </span>
        {/*
          **Só se afirma depois de a sala falar.**

          `safe` vem do `bs`, e a trama de boas-vindas não o diz. Entre a troca
          de um handoff e o primeiro `bs` do destino há até um segundo em que o
          cliente não sabe — e afirmar o valor do quadrante ANTERIOR nesse
          intervalo seria dizer "PORTO SEGURO" a quem acabou de chegar a um
          quadrante perigoso. Um traço não engana ninguém.
        */}
        {border === null ? (
          <span className="open-world-hud__safety" data-testid="open-world-safety">
            —
          </span>
        ) : (
          <span
            className={`open-world-hud__safety${border.safe ? ' is-safe' : ''}`}
            data-testid="open-world-safety"
          >
            {border.safe ? 'PORTO SEGURO' : 'QUADRANTE PERIGOSO'}
          </span>
        )}
        <span className="open-world-hud__health" data-testid="open-world-health">
          VIDA {Math.max(0, Math.min(100, health))}%
        </span>
      </div>

      <div className="open-world-hud__corner open-world-hud__corner--right">
        <span data-testid="open-world-cargo">
          CARGA {cargo?.u ?? 0}/{cargo?.s ?? 0}
        </span>
        <span data-testid="open-world-gold">OURO DE CAMPO {cargo?.g ?? '0'}</span>
        {/*
          A proteção fica aqui porque ela sobrevive à faixa: a espera de três
          minutos corre enquanto se voa pelo meio do quadrante, e é aí que
          saber quanto falta decide se se volta à borda agora ou daqui a pouco.
          A CANALIZAÇÃO não fica: ela só existe dentro do painel da fronteira,
          e contá-la em dois sítios seria dois relógios a discordar.
        */}
        {border !== null ? (
          <span data-testid="open-world-protection">
            {border.p > 0
              ? `PROTEÇÃO ${Math.ceil(border.p / 1000)}s`
              : border.cd > 0
                ? `PROTEÇÃO EM ${Math.ceil(border.cd / 1000)}s`
                : border.safe
                  ? 'ZONA SEGURA'
                  : 'PROTEÇÃO PRONTA'}
          </span>
        ) : null}
      </div>

      {state.notice !== null ? (
        <p className="open-world-hud__notice" data-testid="open-world-notice">
          {state.notice}
        </p>
      ) : null}

      {border?.s === true ? <OpenWorldBorderOptions session={session} border={border} /> : null}
      {/*
        A pesca vive ao lado da fronteira e pela mesma regra: um painel que so
        existe quando ha alguma coisa para fazer ou para ler. Fora de agua, sem
        pescaria e sem resultado por mostrar, ele nao ocupa espaco nenhum.
      */}
      <FishingPanel session={session} manifest={manifest} />
      {/*
        O comerciante vive ao lado da pesca e pela mesma regra: um painel que só
        existe quando há alguma coisa para fazer. Longe de um porto — ou longe do
        balcão dentro dele — ele não ocupa espaço nenhum.
      */}
      <MerchantPanel session={session} manifest={manifest} />
      {/*
        A carga vive ao lado dos outros e sempre: o porão existe em qualquer
        quadrante, e saber o que se traz é o que faz atravessar uma fronteira
        perigosa ser uma decisão em vez de um clique.
      */}
      <CargoPanel session={session} manifest={manifest} />
      {/*
        A caixa aparece quando a autoridade disser que há uma ao alcance, e
        desaparece quando ela deixar de o dizer. O cliente não mede distâncias.
      */}
      <LootPanel session={session} manifest={manifest} />
      {/*
        **A barra das três cartas, no canto inferior esquerdo** (gate V28I.P).

        O MESMO componente da caça e do duelo, com a mesma folha de estilo e a
        mesma ancoragem: o dono aprovou-a lá, e um mundo aberto com uma barra
        diferente seria o jogador a aprender duas interfaces para a mesma coisa.
      */}
      <OpenWorldCardBar session={session} bridge={bridge} />
      {/*
        A PT vive no canto, compacta, e nunca por cima das cartas, da carga, do
        comerciante, da pesca, da fronteira, da caixa ou da coordenada.
      */}
      <OpenWorldPartyHud session={session} />
      {/*
        **O minimapa, permanente e compacto.** Uma janela de três por três à
        volta do quadrante atual, e um botão que abre a grelha inteira. Não
        desenha nada de dentro de um quadrante: é navegação, e não um radar.
      */}
      <OpenWorldMinimap session={session} onOpen={() => setMapOpen(true)} />
      {/*
        **A camada do mapa.** Ela flutua por cima do mundo e não o pausa: a sala
        continua, o combate continua, e a nave fica parada e vulnerável — o que
        o painel diz por escrito, e o que largar os comandos locais garante que
        é verdade também para uma tecla que ficou presa.
      */}
      {mapOpen ? (
        <OpenWorldMapPanel session={session} bridge={bridge} onClose={() => setMapOpen(false)} />
      ) : null}
    </div>
  );
}

/**
 * **A PT durante a expedição.** Uma lista compacta, e privada.
 *
 * ## De onde vem, desde o gate V28I.Q.1
 *
 * Da **vista privada da PT**, e não da trama da sala. A trama descreve com
 * exatidão quem está NESTE quadrante e não sabe nada de quem está noutro — o
 * que era suficiente enquanto a PT voava junta e deixava de o ser no instante
 * em que alguém atravessava uma fronteira. A vista é composta a partir do
 * coordenador de cada conta, que é a autoridade sobre onde uma conta existe, e
 * converge para toda a composição esteja ela onde estiver.
 *
 * ## O que ela mostra
 *
 * Quem, onde, e em que estado. O quadrante é um RÓTULO — `C/5` — e nunca uma
 * posição: uma PT não é uma forma de ver através de paredes, e a área de
 * interesse continua a decidir que entidades existem. `PORTO` ou `PERIGOSO` sai
 * de `isOpenWorldSafePort` no servidor, e não de uma segunda tabela aqui.
 *
 * **Não há vida.** Ninguém a publica para um aliado distante, e inventá-la aqui
 * seria a interface a afirmar uma coisa que nenhuma autoridade disse.
 *
 * ## Porque ela não aparece sozinha
 *
 * Uma expedição de um integrante é uma expedição na mesma, e uma lista com uma
 * linha que diz o que o resto do HUD já diz é ruído. Ela aparece quando há
 * alguém com quem voar.
 */
function OpenWorldPartyHud({
  session,
}: {
  readonly session: OpenWorldSession;
}): ReactElement | null {
  const roster = session.party?.party ?? null;
  if (roster === null || roster.members.length <= 1) {
    return null;
  }
  return (
    <div className="open-world-party-hud" data-testid="open-world-party-hud">
      <h2 className="open-world-party-hud__title">PT</h2>
      <ul>
        {roster.members.map((member) => (
          <li key={member.id}>
            {/* Truncado por CSS; o nome inteiro fica onde um rato e um leitor o leem. */}
            <span className="open-world-party-hud__name" title={member.name}>
              {member.name}
            </span>
            {member.leader ? <span className="open-world-party-hud__tag">LÍDER</span> : null}
            <span className="open-world-party-hud__tag">{partyPlaceText(member)}</span>
            {/* O estado em palavras. A cor reforça; a palavra é a informação. */}
            <span className="open-world-party-hud__tag">{PARTY_STATE_TEXT[member.state]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * **Os três espaços, e o convite para pilotar.**
 *
 * ## O que ela desenha, e de onde vem
 *
 * Do que a sala disse, e de mais nada. Os estados, as prioridades e os motivos
 * vivem em `cardSlotUiViews`, que é o modelo que a barra já sabe ler — o mesmo
 * que a caça e o duelo usam. A tradução do fio para esse modelo é
 * `pvpCardSlotViews`, e ela é literalmente a mesma: a metade privada de uma
 * cadeira tem a mesma forma nas duas arenas, e uma segunda tradução seria uma
 * segunda oportunidade de mostrar "Nível 1" numa carta de nível 6.
 *
 * ## Porque o modo é o do DUELO
 *
 * Porque um quadrante perigoso é PvP total. A pergunta que decide se uma carta
 * está bloqueada é `cardWorksIn(id, 'pvp')`, aqui e na sala — e as duas fazem-na
 * pela mesma função.
 *
 * ## Ela não promete nada
 *
 * A ordem viaja e a barra não espera por ela: o que se desenha é o estado
 * autoritativo, e ele muda quando a autoridade responder. Num porto a sala
 * recusa toda a ativação antes de gastar arrefecimento, e a barra continua a
 * desenhar-se — três caixas que dizem o que está equipado, que é a verdade.
 */
function OpenWorldCardBar({
  session,
  bridge,
}: {
  readonly session: OpenWorldSession;
  readonly bridge: GameBridge;
}): ReactElement {
  const [canBoard, setCanBoard] = useState(false);
  useEffect(
    () => bridge.onUiMessage('open-world:board-offer', ({ available }) => setCanBoard(available)),
    [bridge],
  );
  const me = session.state.entities.find((entity) => entity.id === session.state.me) ?? null;
  const alive = me === null ? false : BigInt(me.healthMilli) > 0n;
  const views = pvpCardSlotViews(session.state.cardSlots, {
    alive,
    /* Há sala a quem dar a ordem enquanto a expedição corre. */
    inMatch: session.state.phase === 'live',
  });
  return (
    <>
      {/*
        **A arte domina a célula** (gate V28I.BX.1). Só aqui: a caça e o duelo
        mantêm a barra que já tinham, porque foi o mundo aberto que o dono julgou.
      */}
      <CardHudBar
        slots={views}
        enabled={session.state.phase === 'live'}
        onActivate={session.activateCard}
        onRelease={session.releaseCard}
        presentation="art"
      />
      {/*
        **O botão de PILOTAR.**

        Só existe enquanto houver uma estrutura encostada e vazia: um botão
        permanente que quase nunca faz nada é ruído. Ele MANDA a ordem e não
        embarca ninguém — a sala relê a posição do casco no tique em que ela
        chega, e é ela que decide.
      */}
      {canBoard ? (
        <button
          type="button"
          className="open-world-board"
          data-testid="open-world-board"
          onClick={session.boardSummon}
        >
          PILOTAR
        </button>
      ) : null}
    </>
  );
}

/**
 * **O painel da fronteira**, mostrado enquanto o casco estiver INTEIRO na faixa.
 *
 * ## O que ele mostra é o que o servidor disse
 *
 * As saídas vêm em `border.o`, uma por direção que existe daqui, cada uma com a
 * coordenada do outro lado. O cliente não deriva vizinhos: ele não sabe onde a
 * grelha acaba nem quais quadrantes são portos, e um botão adivinhado ofereceria
 * uma viagem que a sala depois recusaria. Encostado ao limite exterior do mundo
 * a lista vem vazia, e o painel di-lo por palavras em vez de mostrar setas.
 *
 * ## Porque cada botão diz para onde vai
 *
 * `SUL — B/3` é verificável; `SUL` é uma seta. E um porto diz que é um porto,
 * porque entrar num custa dez segundos parado e sair de um não custa nada.
 */
export function OpenWorldBorderOptions({
  session,
  border,
}: {
  readonly session: OpenWorldSession;
  readonly border: OpenWorldBorderState;
}) {
  const channelling = border.c !== null;
  return (
    <div className="open-world-border" data-testid="open-world-border">
      <header className="open-world-border__head">
        <span className="open-world-border__title">FRONTEIRA</span>
        <span className="open-world-border__protection" data-testid="open-world-border-protection">
          {border.p > 0
            ? `PROTEÇÃO ${Math.ceil(border.p / 1000)}s`
            : border.cd > 0
              ? `PROTEÇÃO EM ${Math.ceil(border.cd / 1000)}s`
              : border.safe
                ? 'ZONA SEGURA'
                : 'PROTEÇÃO PRONTA'}
        </span>
      </header>

      {channelling ? (
        <p className="open-world-border__channel" data-testid="open-world-border-channel">
          {border.c === 'port' ? 'A ENTRAR NO PORTO' : 'A PREPARAR EXTRAÇÃO'}{' '}
          {Math.ceil(border.cr / 1000)}s
        </p>
      ) : (
        <div className="open-world-border__exits">
          {border.o.length === 0 ? (
            <p className="open-world-border__wall" data-testid="open-world-border-wall">
              Esta borda é o limite do mundo. Não há para onde atravessar daqui.
            </p>
          ) : (
            border.o.map((option) => (
              <button
                key={option.d}
                type="button"
                className="open-world-border__exit"
                data-testid={`open-world-travel-${option.d}`}
                onClick={() =>
                  option.ch ? session.requestPortEntry(option.d) : session.requestCrossing(option.d)
                }
              >
                {SIDE_LABELS[option.d]} — {option.p ? 'PORTO ' : ''}
                {openWorldQuadrantLabel({ rowIndex: option.r, columnIndex: option.c })}
                {option.ch ? <em> 10s</em> : null}
              </button>
            ))
          )}
        </div>
      )}

      {border.safe ? (
        /*
          **Num porto não há nada a canalizar** (gate V28I.BZ).

          A autoridade recusa qualquer canalização numa sala segura (`in_port`):
          PREPARAR EXTRAÇÃO seria um botão que nunca funciona, e o CANCELAR o
          travão de uma canalização que não pode começar. No lugar dos dois, o
          que se pode fazer daqui — por palavras, e na classe que este painel já
          usa para dizer que uma borda não leva a lado nenhum.
        */
        <p className="open-world-border__wall" data-testid="open-world-border-port">
          Num porto seguro não há nada a canalizar: a extração prepara-se na fronteira de um
          quadrante perigoso.
        </p>
      ) : (
        <div className="open-world-border__actions">
          {channelling ? null : (
            <button
              type="button"
              className="open-world-border__extract"
              data-testid="open-world-extract"
              onClick={session.requestExtraction}
            >
              PREPARAR EXTRAÇÃO
            </button>
          )}
          {/*
            CANCELAR está sempre presente porque ele é o travão da única coisa
            desta caixa que demora: uma canalização. Fora dela não há nada em
            curso para travar, e por isso ele diz por palavras que não há — em vez
            de desaparecer e deixar o painel a mudar de forma por baixo do rato.
          */}
          <button
            type="button"
            className="open-world-border__cancel"
            data-testid="open-world-cancel"
            disabled={!channelling}
            onClick={session.cancelBorder}
          >
            CANCELAR
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * **O painel da pesca.** Compacto, e por cima de nada que importe.
 *
 * Ele só existe quando há alguma coisa para fazer ou para ler: dentro de uma
 * água, a pescar, ou logo depois de uma tentativa acabar. Fora disso não ocupa
 * espaço nenhum — não cobre a carga, as cartas, a fronteira, a nave nem a ação
 * central, porque nesses instantes ele não está lá.
 *
 * **Tudo o que age é um botão e funciona ao clique.** Não há atalho de teclado:
 * inventar um definitivo sem aprovação seria decidir por quem decide.
 *
 * ## Durante a batalha (gate V28I.X)
 *
 * Espécie, raridade, peso, vida restante, **tempo restante** e `ABANDONAR
 * PESCA`. O relógio é do servidor — ver {@link useFishingCountdown} — e o
 * navegador só o suaviza entre tramas: chegar a zero aqui não acaba pescaria
 * nenhuma, quem a acaba é a sala.
 *
 * O desfecho é sempre dito **por palavras**, e nunca só por uma cor: `O PEIXE
 * ESCAPOU` e `PESCA ABANDONADA` são frases, e leem-se num monitor a preto e
 * branco tal como num a cores.
 */
function FishingPanel({
  session,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly manifest: AssetManifest | null;
}): ReactElement | null {
  const attempt = session.state.fishing;
  const result = session.state.fishingResult;
  const refusal = session.state.fishingRefusal;
  const inWater = session.state.fishingWater !== null;
  /*
   * **Antes de qualquer saída.** Um hook não pode viver depois de um `return`,
   * e o painel tem um: o contador é declarado aqui e vale `null` sozinho quando
   * não há batalha nenhuma.
   */
  const remainingMs = useFishingCountdown(attempt?.ms ?? null);

  if (attempt === null && result === null && refusal === null && !inWater) {
    return null;
  }

  const fish = attempt?.f ?? null;
  const species = fish === null ? null : findFishSpecies(fish.s);
  const permille =
    fish === null || attempt?.h == null
      ? null
      : Number((BigInt(attempt.h) * 1000n) / BigInt(fish.hm));

  return (
    <div className="open-world-fishing" data-testid="open-world-fishing">
      <h2 className="open-world-fishing__title">PESCA</h2>

      {attempt === null ? (
        <button
          type="button"
          className="open-world-fishing__start"
          data-testid="open-world-fish-start"
          onClick={session.startFishing}
        >
          PESCAR
        </button>
      ) : (
        <>
          <p className="open-world-fishing__phase" data-testid="open-world-fish-phase">
            {attempt.p === 'waiting' ? 'AGUARDANDO MORDIDA' : 'BATALHA'}
          </p>
          {/*
            **A arte só DEPOIS da mordida** (gate V28I.BK).
            O que o painel desenha vem de `attempt.f`, e a autoridade só o envia
            quando o peixe morde: em `AGUARDANDO MORDIDA` não há espécie, não há
            peso e não há imagem. Mostrar a arte antes diria ao jogador o que
            está no anzol antes de o servidor lho ter dito — e o segredo antes da
            mordida é do protocolo, não desta folha.
          */}
          {fish === null || species === null ? null : (
            <FishArt
              manifest={manifest}
              speciesId={fish.s}
              alt={species.displayName}
              className="open-world-fishing__art"
            />
          )}
          {fish === null || species === null ? null : (
            <dl className="open-world-fishing__catch">
              <div>
                <dt>Espécie</dt>
                <dd data-testid="open-world-fish-name">{species.displayName}</dd>
              </div>
              <div>
                <dt>Raridade</dt>
                <dd>{species.rarity}</dd>
              </div>
              <div>
                <dt>Peso</dt>
                <dd>{formatWeight(fish.w)}</dd>
              </div>
              <div>
                <dt>Vida</dt>
                <dd data-testid="open-world-fish-health">
                  {attempt.h} / {fish.hm}
                </dd>
              </div>
            </dl>
          )}
          {permille === null ? null : (
            <div className="open-world-fishing__bar" aria-hidden>
              <span style={{ width: `${Math.max(0, Math.min(100, permille / 10))}%` }} />
            </div>
          )}
          {remainingMs === null ? null : (
            <p className="open-world-fishing__clock" data-testid="open-world-fish-clock">
              <span className="open-world-fishing__clock-label">TEMPO RESTANTE</span>{' '}
              <span className="open-world-fishing__clock-value">
                {formatCountdown(remainingMs)}
              </span>
            </p>
          )}
          <button
            type="button"
            className="open-world-fishing__cancel"
            data-testid="open-world-fish-abandon"
            onClick={session.abandonFishing}
          >
            ABANDONAR PESCA
          </button>
        </>
      )}

      {refusal === null ? null : (
        <p className="open-world-fishing__notice" data-testid="open-world-fish-refusal">
          {fishingRefusalText(refusal)}
        </p>
      )}
      {result === null ? null : (
        <p className="open-world-fishing__notice" data-testid="open-world-fish-result">
          {fishingResultText(result)}
        </p>
      )}
    </div>
  );
}

/**
 * **O contador da batalha: o servidor decide, o navegador só suaviza.**
 *
 * ## O que é autoridade e o que é projeção
 *
 * `serverMs` é o tempo que a sala disse que resta, e ele **substitui sempre** a
 * projeção local: cada trama privada re-ancora o contador, o intervalo anterior
 * é cancelado, e o número volta a ser o do servidor. Entre duas tramas o
 * intervalo local desce quatro vezes por segundo para o relógio não saltar de
 * cinquenta em cinquenta milissegundos — e é só isso que ele faz.
 *
 * O navegador **não decide** que o prazo acabou. Chegar a zero aqui não encerra
 * pescaria nenhuma: quem a encerra é a sala, e o que o jogador vê a seguir é a
 * trama `fr` com `escaped`. Um contador local que "acabasse" a pescaria seria o
 * cliente a decidir um desfecho económico.
 *
 * `null` antes da mordida, porque o prazo ainda não existe.
 */
function useFishingCountdown(serverMs: number | null): number | null {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (serverMs === null) {
      setRemaining(null);
      return undefined;
    }
    /* Re-ancorado: o número do servidor manda, e o relógio local recomeça daqui. */
    setRemaining(serverMs);
    const anchoredAt = Date.now();
    const id = window.setInterval(() => {
      setRemaining(Math.max(0, serverMs - (Date.now() - anchoredAt)));
    }, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [serverMs]);

  return remaining;
}

/** O tempo que resta, em minutos e segundos. `2:07`, e nunca `127000 ms`. */
function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** O peso, em quilogramas ou gramas. Inteiro na origem, e legível aqui. */
function formatWeight(weightMg: number): string {
  return weightMg >= 1_000_000
    ? `${(weightMg / 1_000_000).toFixed(2)} kg`
    : `${Math.round(weightMg / 1_000)} g`;
}

/** Porque é que a ordem foi recusada, em português e sem código nenhum. */
function fishingRefusalText(refusal: OpenWorldFishingRefusalFrame): string {
  switch (refusal.r) {
    case 'destroyed':
      return 'A nave não está em condições de pescar.';
    case 'not_in_water':
      return 'É preciso estar dentro de uma área aquática.';
    case 'already_fishing':
      return 'Já há uma pescaria em curso.';
    case 'no_space':
      return 'CARGA CHEIA.';
    case 'busy':
      return 'Não é possível pescar agora.';
    case 'no_fish':
      return refusal.in === null
        ? 'ÁREA SEM PEIXES.'
        : `ÁREA SEM PEIXES. Próximo em ${Math.ceil(refusal.in / 1_000)}s.`;
    default:
      return 'Não foi possível pescar.';
  }
}

/** Como acabou. Uma frase por fim, e o nome do que se apanhou quando há um. */
function fishingResultText(result: OpenWorldFishingResult): string {
  switch (result.e) {
    case 'caught': {
      const species = result.c === null ? null : findFishSpecies(result.c.s);
      return species === null
        ? 'Captura concluída.'
        : `Capturou ${species.displayName} — ${formatWeight(result.c?.w ?? 0)}.`;
    }
    case 'cargo_full':
      return `CARGA CHEIA. Faltava ${result.need ?? 1} espaço.`;
    case 'left_water':
      return 'Saiu da água. O peixe escapou.';
    case 'destroyed':
      return 'A nave caiu. A pescaria acabou.';
    case 'disconnected':
      return 'A ligação caiu. A pescaria acabou.';
    case 'interrupted':
      return 'A pescaria foi interrompida.';
    case 'escaped':
      return 'O PEIXE ESCAPOU. Passaram três minutos e ele continuava vivo.';
    case 'abandoned':
      return 'PESCA ABANDONADA. O peixe escapou.';
    default:
      return 'A pescaria acabou.';
  }
}

/**
 * **O comerciante de pesca.** Duas economias, e a diferença dita por palavras.
 *
 * O painel só existe quando o servidor disser que a nave está perto: `merchantNear`
 * é uma resposta autoritativa, e não uma conclusão do navegador sobre a posição
 * que ele desenha. Sair do alcance, atravessar ou extrair fecha-o, porque o
 * servidor deixa de o dizer.
 *
 * **Nada muda otimisticamente.** Uma linha só desaparece da lista quando o
 * recibo chegar e o balcão for relido — vender é uma coisa que o servidor faz, e
 * mostrar que ela aconteceu antes de ela ter acontecido seria mentir por
 * antecipação.
 */
function MerchantPanel({
  session,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly manifest: AssetManifest | null;
}): ReactElement | null {
  const { merchantNear, merchantView, saleReceipt, saleRefusal } = session.state;
  if (!merchantNear) {
    return null;
  }
  return (
    <div className="open-world-merchant" data-testid="open-world-merchant">
      <h2 className="open-world-merchant__title">COMERCIANTE DE PESCA</h2>

      {merchantView === null ? (
        <button
          type="button"
          className="open-world-merchant__open"
          data-testid="open-world-merchant-open"
          onClick={session.openMerchant}
        >
          NEGOCIAR
        </button>
      ) : (
        <>
          <SaleSection
            title="CARGA DE CAMPO"
            warning="Recebe Ouro de campo — pode ser perdido se a nave for destruída."
            scope="field"
            lines={merchantView.f}
            session={session}
            manifest={manifest}
          />
          <SaleSection
            title="COLEÇÃO SEGURA"
            warning="Recebe Ouro seguro — permanece na conta."
            scope="vault"
            lines={merchantView.v}
            session={session}
            manifest={manifest}
            more={merchantView.more}
          />
          <p className="open-world-merchant__version">Tabela de preços v{merchantView.pv}</p>
        </>
      )}

      {saleRefusal === null ? null : (
        <p className="open-world-merchant__notice" data-testid="open-world-sale-refusal">
          {saleRefusalText(saleRefusal.r)}
        </p>
      )}
      {saleReceipt === null ? null : (
        <p className="open-world-merchant__notice" data-testid="open-world-sale-receipt">
          Vendeu {saleReceipt.n} {saleReceipt.n === 1 ? 'captura' : 'capturas'} por {saleReceipt.g}{' '}
          Ouro
          {saleReceipt.d === 'safe_wallet' ? ' seguro' : ' de campo'} (tabela v{saleReceipt.pv}).
        </p>
      )}
    </div>
  );
}

/** Uma das duas secções. A mesma forma, e o aviso que as distingue. */
function SaleSection({
  title,
  warning,
  scope,
  lines,
  session,
  manifest,
  more,
}: {
  readonly title: string;
  readonly warning: string;
  readonly scope: 'field' | 'vault';
  readonly lines: readonly OpenWorldSaleLine[];
  readonly session: OpenWorldSession;
  readonly manifest: AssetManifest | null;
  readonly more?: boolean;
}): ReactElement {
  return (
    <section className="open-world-merchant__section">
      <h3>{title}</h3>
      <p className="open-world-merchant__warning">{warning}</p>
      {lines.length === 0 ? (
        <p className="open-world-merchant__empty">Nada para vender aqui.</p>
      ) : (
        <>
          <ul className="open-world-merchant__lines">
            {lines.map((line) => {
              const species = findFishSpecies(line.s);
              return (
                <li key={line.i}>
                  {/*
                    A arte da espécie, quando ela tem (gate V28I.BK). O nome
                    fica sempre ao lado — uma retirada continua a ler-se, a
                    pesar-se e a vender-se pelo preço certo sem imagem nenhuma.

                    O invólucro existe SEMPRE, mesmo vazio: a linha é uma grelha
                    de colunas fixas, e um filho que aparece e desaparece
                    arrastaria o nome, o peso e o preço uma coluna para o lado
                    conforme a espécie tivesse arte ou não.
                  */}
                  <span className="open-world-merchant__art">
                    <FishArt
                      manifest={manifest}
                      speciesId={line.s}
                      alt={species?.displayName ?? line.s}
                      className="open-world-merchant__art-image"
                    />
                  </span>
                  <span className="open-world-merchant__name">
                    {species?.displayName ?? line.s}
                  </span>
                  <span className="open-world-merchant__rarity">{line.r}</span>
                  <span className="open-world-merchant__weight">{formatWeight(line.w)}</span>
                  <span className="open-world-merchant__price">{line.p} Ouro</span>
                  <button
                    type="button"
                    onClick={() => {
                      session.sell(scope, [line.i]);
                    }}
                  >
                    VENDER
                  </button>
                </li>
              );
            })}
          </ul>
          {more === true ? (
            <p className="open-world-merchant__empty">
              Há mais capturas do que as listadas. VENDER TODOS vende as elegíveis.
            </p>
          ) : null}
          <button
            type="button"
            className="open-world-merchant__all"
            data-testid={`open-world-sell-all-${scope}`}
            onClick={() => {
              /*
               * **Sem lista.** Quem determina outra vez as linhas elegíveis é a
               * autoridade — o navegador não escolhe o que vai ser vendido.
               */
              session.sell(scope, null);
            }}
          >
            VENDER TODOS
          </button>
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  A carga de campo, e as caixas de morte                                    */
/* -------------------------------------------------------------------------- */

/**
 * O nome de cada categoria, em português.
 *
 * `Readonly<Record<ItemCategory, string>>` e não um mapa aberto: uma categoria
 * nova no catálogo **não compila** aqui até alguém lhe dar um nome. Um painel
 * que mostrasse `jewel` ao jogador seria um painel que não sabia nomear o que
 * está a mostrar, e descobrir isso em produção é tarde.
 */
const FIELD_CATEGORY_LABEL: Readonly<Record<ItemCategory, string>> = Object.freeze({
  ship: 'Nave',
  cannon: 'Canhão',
  artifact: 'Artefato',
  pet: 'Pet',
  skin: 'Skin',
  consumable: 'Consumível',
  equipment: 'Equipamento',
  jewel: 'Joia',
  card: 'Carta',
});

/**
 * **Quantos, quando são muitos.** `null` quando o item não é uma pilha.
 *
 * O descritor vem das propriedades congeladas da linha, e um descritor
 * malformado não deita o painel abaixo: aqui isto é apresentação, e uma lista em
 * branco por causa de uma linha estranha seria pior do que uma linha sem
 * quantidade. Nada de económico se decide nesta função.
 */
function stackQuantityOf(item: OpenWorldFieldItem): number | null {
  try {
    const reward = decodeOpenWorldFieldReward(item.properties);
    return reward !== null && reward.k === 'stack' ? reward.q : null;
  } catch {
    return null;
  }
}

/**
 * **A imagem de um item da carga**, ou o recuo, e nunca um caminho à mão.
 *
 * Os três manifestos vivem atrás de `itemArt`, que é o único sítio onde uma
 * definição vira uma imagem: o geral resolve-se por chave, e os das cartas e das
 * joias por caminho, com o `baseUrl` do manifesto à frente. Uma definição sem
 * arte — hoje as rações, as esferas, os baldes e o Estabilizador — não desenha
 * nada, e a linha continua legível pelo nome.
 */
function FieldItemArt({
  manifest,
  definitionId,
  level,
  alt,
}: {
  readonly manifest: AssetManifest | null;
  readonly definitionId: string;
  readonly level: number;
  readonly alt: string;
}): ReactElement | null {
  if (manifest === null) {
    return null;
  }
  /*
   * O MESMO passo que a célula do inventário e o mercado usam (V28I.BI.2).
   * Ele resolve pelos quatro manifestos e devolve `null` para uma definição
   * que nenhum deles descreve — nunca um pedido partido.
   */
  const src = itemArtUrl(manifest, definitionId, level);
  if (src === null) {
    return null;
  }
  return (
    <img className="open-world-cargo__art" src={src} alt={alt} loading="lazy" draggable={false} />
  );
}

/**
 * **A arte de uma espécie, ou nada** (gate V28I.BK).
 *
 * O gémeo de {@link FieldItemArt}, e a mesma regra: um único passo — `fishArtUrl`
 * —, nenhum caminho composto à mão, e `null` quando não há arte. Não há recuo
 * desenhado aqui porque não há nenhum a desenhar: **o nome está sempre ao lado**,
 * e é ele que diz o que o exemplar é.
 *
 * As vinte espécies RETIRADAS caem neste `null`, e é o comportamento honesto:
 * elas nunca tiveram arte, a linha continua a mostrar o nome certo, o peso certo
 * e o preço certo, e nada desaparece do porão por falta de uma imagem.
 */
function FishArt({
  manifest,
  speciesId,
  alt,
  className,
}: {
  readonly manifest: AssetManifest | null;
  readonly speciesId: string;
  readonly alt: string;
  readonly className: string;
}): ReactElement | null {
  if (manifest === null) {
    return null;
  }
  const src = fishArtUrl(manifest, speciesId);
  if (src === null) {
    return null;
  }
  return <img className={className} src={src} alt={alt} loading="lazy" draggable={false} />;
}

/** Uma linha de item, na mesma forma na carga e dentro de uma caixa. */
function FieldItemLine({
  item,
  manifest,
  action,
}: {
  readonly item: OpenWorldFieldItem;
  readonly manifest: AssetManifest | null;
  readonly action?: ReactElement;
}): ReactElement {
  const species = item.speciesId === null ? null : findFishSpecies(item.speciesId);
  const quantity = stackQuantityOf(item);
  /*
   * **O nome canónico, e nunca o `definitionId`** (gate V28I.BI).
   *
   * `itemDisplayName` é o único sítio onde uma definição vira uma palavra, e
   * responde por todas: joias, cartas, equipamento, naves, artefactos, pets e
   * consumíveis. Um id desconhecido devolve "Item desconhecido" em vez de
   * `zztest_relic` — o id cru pertence a um aviso de desenvolvimento, e não a
   * um jogador que está a decidir se arrisca a carga.
   *
   * O catálogo é público e local: nada disto move uma versão de protocolo.
   */
  const name =
    item.kind === 'creature'
      ? (species?.displayName ?? 'Captura desconhecida')
      : item.definitionId === null
        ? '—'
        : itemDisplayName(item.definitionId);
  return (
    <li className="open-world-cargo__item">
      {/*
        A MESMA célula para as duas metades do porão (gate V28I.BK): um item
        resolve por `itemArtUrl`, uma captura por `fishArtUrl`, e o resultado
        ocupa o mesmo lugar com o mesmo tamanho. Um porão onde só metade das
        linhas tem imagem parece um porão meio carregado.
      */}
      {item.kind === 'creature' ? (
        item.speciesId === null ? null : (
          <FishArt
            manifest={manifest}
            speciesId={item.speciesId}
            alt={name}
            className="open-world-cargo__art"
          />
        )
      ) : item.definitionId === null ? null : (
        <FieldItemArt
          manifest={manifest}
          definitionId={item.definitionId}
          level={item.level}
          alt={name}
        />
      )}
      <span className="open-world-cargo__name">{name}</span>
      {/*
        A categoria e a raridade viajam por palavras, e nunca só por uma cor: um
        jogador que não distinga o ciano do dourado tem de conseguir ler a mesma
        coisa que os outros. A cor é um reforço, e não a informação.
      */}
      <span className="open-world-cargo__tag">
        {item.kind === 'creature'
          ? 'Captura'
          : item.category === null
            ? '—'
            : FIELD_CATEGORY_LABEL[item.category]}
      </span>
      <span className="open-world-cargo__tag">{rarityLabel(item.rarity)}</span>
      {item.kind === 'creature' && item.weightMilligrams !== null ? (
        <span className="open-world-cargo__tag">{formatWeight(item.weightMilligrams)}</span>
      ) : (
        <span className="open-world-cargo__tag">Nível {item.level}</span>
      )}
      {quantity === null ? null : <span className="open-world-cargo__tag">×{quantity}</span>}
      {action ?? null}
    </li>
  );
}

/**
 * **O porão desta nave, só de leitura.**
 *
 * ## Porque só de leitura
 *
 * Não há descartar, guardar, vender daqui, equipar, consumir, reordenar nem
 * passar a um aliado — e nenhuma dessas coisas tem uma intenção no protocolo,
 * o que é a forma de elas não existirem em vez da forma de serem recusadas.
 * Este painel mostra; quem move propriedade é o servidor, e só pelas ordens que
 * existem.
 *
 * ## Porque ele pergunta em vez de guardar
 *
 * A sala não tem a lista — tem contagens. A lista vem da autoridade quando o
 * painel abre, e volta a vir sempre que a **revisão** da carga muda, que é o
 * sinal de que alguma coisa entrou ou saiu. Uma lista mantida em memória do lado
 * do cliente estaria errada no instante seguinte a um saque.
 */
function CargoPanel({
  session,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly manifest: AssetManifest | null;
}): ReactElement | null {
  const { cargo, cargoItems } = session.state;
  const [open, setOpen] = useState(false);
  const revision = cargo?.r ?? null;
  const { inspectCargo } = session;

  useEffect(() => {
    if (!open || revision === null) {
      return;
    }
    /*
     * Abrir pergunta, e cada revisão nova volta a perguntar. `revision` está nas
     * dependências exatamente por isso: é a autoridade a dizer que a lista de
     * ontem já não serve.
     */
    inspectCargo();
  }, [open, revision, inspectCargo]);

  if (cargo === null) {
    return null;
  }

  return (
    <div className="open-world-cargo" data-testid="open-world-cargo-panel">
      <button
        type="button"
        className="open-world-cargo__toggle"
        data-testid="open-world-cargo-toggle"
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {open ? 'FECHAR CARGA' : 'CARGA DE CAMPO'}
      </button>

      {!open ? null : (
        <>
          <p className="open-world-cargo__counts">
            {cargo.u}/{cargo.s} espaços · {cargo.g} Ouro de campo
          </p>
          <p className="open-world-cargo__warning">
            Tudo o que está aqui — itens e Ouro de campo — cai numa caixa pública se a nave for
            destruída.
          </p>
          {cargoItems === null ? (
            <p className="open-world-cargo__empty">A ler a carga…</p>
          ) : cargoItems.length === 0 ? (
            <p className="open-world-cargo__empty">O porão está vazio.</p>
          ) : (
            <ul className="open-world-cargo__list">
              {cargoItems.map((item) => (
                <FieldItemLine key={item.fieldItemId} item={item} manifest={manifest} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/**
 * **A caixa que alguém deixou ao morrer.**
 *
 * ## O que o navegador diz, e o que ele não diz
 *
 * Ele diz **qual** caixa quer abrir, e mais nada. Não afirma distância, não
 * afirma propriedade, não afirma o conteúdo, a capacidade, a quantidade, o Ouro
 * nem o sucesso: o servidor relê a posição do casco e valida o id que recebeu.
 * O botão só existe porque a autoridade disse que existe uma caixa ao alcance.
 *
 * ## Nada muda por antecipação
 *
 * Um item só desaparece da lista quando a caixa for relida pela autoridade. Duas
 * pessoas podem ter a mesma caixa aberta ao mesmo tempo, e cada item vai só a
 * uma delas — o painel de quem perdeu a corrida converge na trama seguinte, e
 * dizer o contrário antes disso seria prometer espólio que não é de quem olha.
 */
function LootPanel({
  session,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly manifest: AssetManifest | null;
}): ReactElement | null {
  const { nearestBox, boxView, lootResult, cargo } = session.state;
  if (nearestBox === null && boxView === null) {
    return null;
  }
  const free = cargo === null ? null : cargo.s - cargo.u;

  return (
    <div className="open-world-loot" data-testid="open-world-loot">
      <h2 className="open-world-loot__title">CAIXA DE MORTE</h2>

      {boxView === null ? (
        <button
          type="button"
          className="open-world-loot__open"
          data-testid="open-world-loot-open"
          onClick={() => {
            if (nearestBox !== null) {
              session.openBox(nearestBox);
            }
          }}
        >
          ABRIR CAIXA
        </button>
      ) : (
        <>
          <p className="open-world-loot__counts">
            {boxView.g} Ouro · {boxView.i.length} {boxView.i.length === 1 ? 'item' : 'itens'} ·{' '}
            {free ?? '—'} espaços livres na nave · expira em {Math.ceil(boxView.ttl / 1000)}s
          </p>

          {boxView.i.length === 0 ? (
            <p className="open-world-loot__empty">Não sobrou nenhum item.</p>
          ) : (
            <ul className="open-world-cargo__list">
              {boxView.i.map((item) => (
                <FieldItemLine
                  key={item.fieldItemId}
                  item={item}
                  manifest={manifest}
                  action={
                    <button
                      type="button"
                      onClick={() => {
                        session.takeItem(boxView.b, item.fieldItemId);
                      }}
                    >
                      PEGAR
                    </button>
                  }
                />
              ))}
            </ul>
          )}

          <div className="open-world-loot__actions">
            {/*
              **`PEGAR TODOS OS ITENS`**, e o nome é literal: a ordem `la` leva
              os itens que couberem e o Ouro tem ordem própria. Chamar-lhe
              "PEGAR TUDO" seria dar-lhe uma semântica que o servidor não tem.
            */}
            <button
              type="button"
              data-testid="open-world-loot-take-all"
              onClick={() => {
                session.takeAllItems(boxView.b);
              }}
            >
              PEGAR TODOS OS ITENS
            </button>
            <button
              type="button"
              data-testid="open-world-loot-take-gold"
              onClick={() => {
                session.takeGold(boxView.b);
              }}
            >
              PEGAR OURO
            </button>
            <button type="button" data-testid="open-world-loot-close" onClick={session.closeBox}>
              FECHAR
            </button>
          </div>
        </>
      )}

      {lootResult === null ? null : (
        <p className="open-world-loot__notice" data-testid="open-world-loot-result">
          {lootResultText(lootResult)}
        </p>
      )}
    </div>
  );
}

/** O que a última ordem produziu, dito por palavras e nunca por um código. */
function lootResultText(result: OpenWorldLootResult): string {
  if (result.r !== null) {
    return lootRefusalText(result, result.r);
  }
  const parts: string[] = [];
  if (result.got.length > 0) {
    parts.push(`${result.got.length} ${result.got.length === 1 ? 'item' : 'itens'}`);
  }
  if (result.gold !== '0') {
    parts.push(`${result.gold} Ouro`);
  }
  if (parts.length === 0) {
    return 'Nada foi levado.';
  }
  const left = result.left > 0 ? ` Ficaram ${result.left} por falta de espaço.` : '';
  return `Levou ${parts.join(' e ')}.${left}`;
}

/** Porque não. A lista é fechada, e nenhuma delas é "não sei". */
function lootRefusalText(result: OpenWorldLootResult, refusal: OpenWorldLootRefusal): string {
  switch (refusal) {
    case 'no_box':
      return 'A caixa já não está aqui.';
    case 'wrong_quadrant':
      return 'Essa caixa fica noutro quadrante.';
    case 'out_of_range':
      return 'Aproxime-se da caixa.';
    case 'destroyed':
      return 'Uma nave destruída não saqueia.';
    case 'already_taken':
      return 'Alguém chegou primeiro a esse item.';
    case 'no_space':
      return result.need === null
        ? 'Não há espaço na carga.'
        : `Faltam ${result.need} ${result.need === 1 ? 'espaço' : 'espaços'} na carga.`;
    case 'no_gold':
      return 'Esta caixa não tem Ouro.';
    case 'not_looting':
      return 'Abra a caixa antes de tirar dela.';
    case 'unavailable':
      return 'Não foi possível saquear agora. Nada mudou.';
  }
}

/** Porque é que a ordem foi recusada, em português e sem código nenhum. */
function saleRefusalText(refusal: OpenWorldSaleRefusal): string {
  switch (refusal) {
    case 'no_merchant':
      return 'Não há comerciante neste quadrante.';
    case 'too_far':
      return 'Aproxime-se do comerciante para negociar.';
    case 'busy':
      return 'Não é possível negociar agora.';
    case 'nothing_to_sell':
      return 'Não havia nada para vender.';
    case 'overflow':
      return 'A carteira ficaria acima do limite. Nada foi vendido.';
    case 'unavailable':
      return 'Não foi possível confirmar a venda. Tente outra vez.';
    default:
      return 'Não foi possível vender.';
  }
}

/**
 * **O relatório da expedição.** Funcional, e sem arte final.
 *
 * Diz o que aconteceu e para onde se vai: o Ouro que passou a ser seguro, os
 * itens e as capturas transferidas, e onde ficou a caixa quando houve uma.
 *
 * **Não conta mobs, bosses nem recursos** — e a razão mudou. Eles existem desde
 * a V28I.K, a V28I.L e a V28I.M, e são abatidos e partidos numa expedição
 * qualquer; o que não existe é a DECISÃO de os contar. O relatório versionado
 * deixou o degrau pronto (`ext`, em `open_world_expedition_reports`), vazio de
 * propósito: criar colunas a zero para contadores que ninguém pediu seria
 * inventar conteúdo, e mostrar um número que a transação não escreveu seria
 * mostrar um número que ninguém garantiu.
 */
export function OpenWorldReceipt({
  session,
  onReturn,
}: {
  readonly session: OpenWorldSession;
  readonly onReturn: () => void;
}) {
  const receipt = session.state.receipt;
  if (receipt === null) {
    return null;
  }
  const died = receipt.outcome === 'died';
  /*
   * **O botão só existe depois de a autoridade libertar a localização**
   * (gate V28I.AV).
   *
   * `to` é `null` até `location_released`, e antes disso esta conta ainda
   * pertence a um quadrante. Um clique local não pode fingir que ela já saiu:
   * seria convidar o piloto a estar nos dois sítios, e o que ele veria a seguir
   * era uma caça que a autoridade ainda não lhe deu.
   */
  const ready = receipt.to === 'hunt';
  const box = receipt.box;
  return (
    <section
      className="open-world-receipt"
      data-testid="open-world-receipt"
      data-outcome={receipt.outcome}
    >
      <h2>{died ? 'NAVE DESTRUÍDA' : 'EXPEDIÇÃO CONCLUÍDA'}</h2>
      {died ? (
        <>
          {box === null ? (
            /*
             * Sem carga não há caixa, e não se cria uma vazia. O piloto perdeu
             * a nave e mais nada, e é isso que a frase diz.
             */
            <p data-testid="open-world-receipt-loss">Nenhuma carga de campo foi perdida.</p>
          ) : (
            <>
              <p data-testid="open-world-receipt-lost-gold">Ouro de campo perdido: {box.gold}</p>
              <p data-testid="open-world-receipt-lost-items">
                Itens deixados na caixa: {box.items}
              </p>
              <p data-testid="open-world-receipt-box">
                A caixa ficou no quadrante {String.fromCharCode(65 + box.rowIndex)}/
                {box.columnIndex + 1}. Ela é <strong>pública</strong> — qualquer piloto pode
                saqueá-la — e desaparece quinze minutos depois da morte.
              </p>
            </>
          )}
          <p data-testid="open-world-receipt-safe">
            O inventário principal, o Ouro seguro, os equipamentos, as cartas e a progressão não
            foram afectados.
          </p>
        </>
      ) : (
        <>
          <p data-testid="open-world-receipt-gold">Ouro transferido: {receipt.gold}</p>
          <p data-testid="open-world-receipt-items">Itens transferidos: {receipt.items}</p>
          <p data-testid="open-world-receipt-catches">Capturas transferidas: {receipt.catches}</p>
        </>
      )}
      {ready ? null : <p data-testid="open-world-receipt-closing">A fechar a expedição…</p>}
      <button type="button" data-testid="open-world-return" onClick={onReturn} disabled={!ready}>
        VOLTAR À CAÇA
      </button>
    </section>
  );
}

/**
 * **A última expedição acabada, no painel de entrada** (gate V28I.BY).
 *
 * Um recibo é uma linha que já existe, e repeti-lo não repete nada. É aqui que o
 * piloto o encontra quando a resposta do fim se perdeu pelo caminho, ou quando a
 * autoridade acabou de fechar uma expedição que ainda aparecia como ativa — em vez
 * de um RETOMAR que o devolvia a uma expedição sem carga.
 */
function OpenWorldLastReceipt({ receipt }: { readonly receipt: OpenWorldExtractionReceipt }) {
  const died = receipt.outcome === 'died';
  return (
    <div
      className="open-world-panel__last"
      data-testid="open-world-last-receipt"
      data-outcome={receipt.outcome}
    >
      <p>
        <strong>
          {died ? 'Última expedição: nave destruída.' : 'Última expedição concluída.'}
        </strong>
      </p>
      {died ? (
        <p>
          {receipt.box === null
            ? 'Nenhuma carga de campo foi perdida.'
            : `Ouro de campo perdido: ${receipt.box.gold} · itens deixados na caixa: ${receipt.box.items}`}
        </p>
      ) : (
        <p>
          Ouro transferido: {receipt.gold} · itens: {receipt.items} · capturas: {receipt.catches}
        </p>
      )}
    </div>
  );
}

/** O anfitrião: decide qual das três coisas está no ecrã, e mais nada. */
export function OpenWorldFeature({
  session,
  open,
  onClose,
  bridge,
  manifest,
}: {
  readonly session: OpenWorldSession;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly bridge: GameBridge;
  /** O manifesto geral. `null` enquanto ele não chegou; ver `OpenWorldHud`. */
  readonly manifest: AssetManifest | null;
}) {
  useEffect(
    () =>
      bridge.onUiMessage('open-world:scene-error', (payload) => {
        console.error(`open world scene failed at ${payload.stage}: ${payload.message}`);
      }),
    [bridge],
  );

  if (session.state.receipt !== null) {
    return <OpenWorldReceipt session={session} onReturn={session.leave} />;
  }
  if (
    session.state.phase === 'live' ||
    session.state.phase === 'crossing' ||
    session.state.phase === 'reconnecting'
  ) {
    return <OpenWorldHud session={session} bridge={bridge} manifest={manifest} />;
  }
  return open ? <OpenWorldEntryPanel session={session} onClose={onClose} /> : null;
}
